import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Trophy, Target, TrendingUp, AlertTriangle, Ban, Building2 } from "lucide-react";
import RankingVendedores from "../components/RankingVendedores";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function StatusBadge({ pct, semMeta }) {
  if (semMeta) {
    return (
      <Badge className="bg-zinc-200 text-zinc-700 hover:bg-zinc-200 gap-1" data-testid="badge-sem-meta">
        <Ban size={11} /> Meta não cadastrada
      </Badge>
    );
  }
  if (pct >= 100) {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600 gap-1" data-testid="badge-meta-batida">
        <Trophy size={11} /> Meta batida
      </Badge>
    );
  }
  if (pct >= 70) {
    return (
      <Badge className="bg-amber-500 text-white hover:bg-amber-500 gap-1" data-testid="badge-em-evolucao">
        <TrendingUp size={11} /> Em evolução
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-600 text-white hover:bg-red-600 gap-1" data-testid="badge-atencao">
      <AlertTriangle size={11} /> Atenção
    </Badge>
  );
}

export default function Metas() {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [unidade, setUnidade] = useState("__todas__");
  const [unidades, setUnidades] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const podeFiltrarUnidade = user && (user.role === "diretor" || user.role === "supervisao") && user.escopo === "geral";

  useEffect(() => {
    if (podeFiltrarUnidade) {
      api.get("/comercial/unidades").then((r) => setUnidades(r.data || [])).catch(() => setUnidades([]));
    }
  }, [podeFiltrarUnidade]);

  useEffect(() => {
    setLoading(true);
    const params = { mes, ano };
    if (unidade !== "__todas__") params.unidade_id = unidade;
    // FONTE ÚNICA — mesmo endpoint do ranking → garante consistência total
    api.get("/comercial/comissoes/ranking", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mes, ano, unidade]);

  const items = data?.items || [];

  return (
    <div className="space-y-5" data-testid="metas-page">
      <div>
        <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Metas</h1>
        <p className="text-sm text-muted-foreground mt-1">Acompanhe vendedores, metas e ranking competitivo do mês</p>
      </div>

      <Tabs defaultValue="cadastradas" className="w-full">
        <TabsList>
          <TabsTrigger value="cadastradas" data-testid="tab-cadastradas"><Target size={14} className="mr-1" /> Metas cadastradas</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-ranking"><Trophy size={14} className="mr-1" /> Ranking competitivo</TabsTrigger>
        </TabsList>

        <TabsContent value="cadastradas" className="space-y-4 mt-4">
          {/* Filtros */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs uppercase">Mês</Label>
              <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
                <SelectTrigger className="w-[110px]" data-testid="metas-mes"><SelectValue /></SelectTrigger>
                <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Ano</Label>
              <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
                <SelectTrigger className="w-[100px]" data-testid="metas-ano"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {podeFiltrarUnidade && (
              <div>
                <Label className="text-xs uppercase">Unidade</Label>
                <Select value={unidade} onValueChange={setUnidade}>
                  <SelectTrigger className="w-[180px]" data-testid="metas-unidade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__todas__">Todas</SelectItem>
                    {unidades.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} ({u.estado})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground ml-auto self-center">
              Fonte unificada com o ranking · Dados em tempo real
            </p>
          </div>

          {loading ? (
            <Card className="p-10 text-center text-muted-foreground">Carregando…</Card>
          ) : items.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">Nenhum vendedor neste período.</Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((m) => {
                const isMe = user?.id === m.vendedor_id;
                const semMeta = !m.meta_fechamentos || m.meta_fechamentos === 0;
                const pct = semMeta ? 0 : Math.min(100, Math.round(m.pct_meta));
                return (
                  <Card
                    key={m.vendedor_id}
                    className={`p-5 relative ${isMe ? "ring-2 ring-orange-500 border-orange-300" : ""}`}
                    data-testid={`meta-card-${m.vendedor_id}`}
                  >
                    {isMe && (
                      <Badge className="absolute -top-2 -left-2 bg-orange-600 text-white text-[10px]" data-testid="badge-voce">
                        VOCÊ
                      </Badge>
                    )}
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {m.iniciais}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {m.posicao <= 3 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 border-orange-300 text-orange-700">
                                <Trophy size={10} />#{m.posicao}
                              </Badge>
                            )}
                            {m.posicao > 3 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                                #{m.posicao}
                              </Badge>
                            )}
                            <h3 className="font-display font-bold text-base truncate" style={{ color: "#1B3A6B" }}>
                              {m.vendedor_nome}
                            </h3>
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <span className="capitalize">{m.vendedor_role}</span>
                            {m.unidade_nome && (
                              <>
                                <span>·</span>
                                <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0">
                                  <Building2 size={10} />{m.unidade_nome} {m.unidade_estado && `(${m.unidade_estado})`}
                                </Badge>
                              </>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {MESES[mes - 1]}/{ano}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {semMeta ? (
                          <div className="text-xl font-display font-bold text-zinc-400">—</div>
                        ) : (
                          <div
                            className="text-3xl font-display font-bold leading-none"
                            style={{ color: pct >= 100 ? "#15803D" : pct >= 70 ? "#B45309" : "#DC2626" }}
                          >
                            {pct}%
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground mt-1">% meta</div>
                      </div>
                    </div>

                    {semMeta ? (
                      <div className="bg-zinc-50 border border-zinc-200 rounded p-2.5 text-xs text-zinc-600" data-testid="aviso-sem-meta">
                        <Ban size={12} className="inline mr-1" />
                        Meta não cadastrada para este período.
                      </div>
                    ) : (
                      <Progress value={pct} className="h-2.5" />
                    )}

                    <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                      <div>
                        <div className="text-muted-foreground uppercase">Fechamentos</div>
                        <div className="font-semibold text-foreground">
                          {m.fechamentos_realizados}
                          {!semMeta && <span className="text-muted-foreground">/{m.meta_fechamentos}</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground uppercase">Contratos</div>
                        <div className="font-semibold text-foreground">{m.qtd_contratos}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-muted-foreground uppercase">Comissão</div>
                        <div className="font-bold" style={{ color: "#1B3A6B" }}>{fmt(m.valor_total)}</div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-end">
                      <StatusBadge pct={pct} semMeta={semMeta} />
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ranking" className="mt-4">
          <RankingVendedores />
        </TabsContent>
      </Tabs>
    </div>
  );
}
